import { useDebounceFn } from '@vueuse/core'
import { createStorage, readJSON, writeJSON } from './storage'
import { functions, Logger } from '@/util'

const log = Logger.get('yb.storage.vuex')

let store
const workspaceRoot = 'workspace'
const workspaceFile = `${workspaceRoot}/settings`
const workspaceTabsRoot = `${workspaceRoot}/query-editor/tabs`
const propSettings = 'settings'

function sqlSecondaryPath(tabId) {
  return `${workspaceTabsRoot}/${tabId}/sql`
}

const setsEqual = (a, b) =>
  a.size === b.size && [...a].every(v => b.has(v))

class VuexLocalStorage {
  constructor(appName) {
    this._appName = appName || 'yb.settings'
    this._username = null
    this._tabKeys = new Set()
    this._hasReadStorage = false
    this._createStorage = null
    this._storage = null
    this._save = useDebounceFn(async (state) => {
      try {
        await this.saveImpl(state)
      } catch (e) {
        log.error('Could not save settings:', e)
      }
    }, 10) // buffered writing happens in storage
  }

  get appName() {
    return this._appName
  }

  set appName(_) {
    this._appName = _
  }

  get userName() {
    return this._username
  }

  async setUserName(_) {
    this._username = _
    if (!!this._username) {
      await this.restore()
    }
  }

  get key() {
    return this.appName
  }

  get storage() {
    return this._storage
  }

  async createStorage() {
    if (!this._createStorage) {
      this._createStorage = createStorage(this._username)
        .then((storage) => {
          this._storage = storage
        })
    }
    await this._createStorage
  }

  plugin(_) {
    store = _

    // Restore the state.
    this.restore()

    // Listen for changes on the store.
    store.subscribe(this.persist.bind(this))

    // Add hooks for query editor.
    store.$queryEditorReadSQL = async (key) => {
      await this.createStorage()
      return await this.storage.readPath(this.key, sqlSecondaryPath(key))
    }
    store.$queryEditorWriteSQL = async (key, sql) => {
      await this.createStorage()
      await this.storage.writePath(this.key, sqlSecondaryPath(key), sql)
    }

    // Add hook for storage
    Object.defineProperty(store,
      '$storage', {
        get: () => this.storage
      })
  }

  persist(mutation, state) {
    // Only care about mutation type "settings"
    if (mutation && !String(mutation.type).match(/settings/i)) {
      return
    }

    if (this._hasReadStorage) {
      this.save(state)
    }
  }

  save(state) {
    this._save(state)
  }

  async saveImpl(state, convertSQL) {
    // Only persist if we are logged in.
    if (!this._username) {
      return
    }

    // Find only settings and copy them here.
    function extractSettings(s, target, hadSettings) {
      if (hadSettings > 10) {
        throw new Error('Settings nesting limit reached')
      }
      if (typeof s.settings !== 'undefined') {
        target.settings = s.settings
        hadSettings = 0
      } else if (hadSettings > 2) {
        return
      }
      for (const p in s) {
        if (p !== propSettings) {
          const obj = s[p]
          if (obj && typeof obj === 'object') {
            const sub = extractSettings(obj, {}, hadSettings + 1)
            if (sub) {
              target[p] = sub
            }
          }
        }
      }
      return target.settings ? target : null
    }
    const settings = extractSettings(state, {}, 0)

    // Collect the writes here.
    const writes = []

    // Remove the .sql from main settings.
    const tabKeys = new Set()
    const clone = functions.copy(settings)
    const tabs = clone?.['query-editor']?.settings?.tabs ?? []
    for (const tab of tabs) {
      tabKeys.add(tab.key)

      // Store secondary (convert from local storage to secondary storage)
      if (convertSQL && typeof tab.settings?.sql === 'string') {
        writes.push(this.storage.writePath(this.key, sqlSecondaryPath(tab.key), tab.settings.sql))
      }
      delete tab.settings.sql
    }

    // Garbage collect secondary tab storage we have orphaned if the set of tabs has changed.
    if (!setsEqual(this._tabKeys, tabKeys)) {
      this._tabKeys = tabKeys
      const storedTabs = await this.storage.listPath(this.key, workspaceTabsRoot)
      storedTabs.forEach((storedTab) => {
        if (!tabKeys.has(storedTab)) {
          writes.push(this.storage.removePath(this.key, `${workspaceTabsRoot}/${storedTab}`))
        }
      })
    }

    // Do the writes.
    await Promise.all([...writes, writeJSON(this.storage, this.key, workspaceFile, clone)])
  }

  async restore() {
    if (!this._username || !!this._hasReadStorage) {
      return
    }
    const { key } = this

    // Restore persisted store settings.
    try {
      // Ensure storage is created.
      await this.createStorage()

      // Attempt restore from legacy localStorage, which will be converted to storage interface.
      const localStorageKey = `${this._appName}:${this._username}`
      let storedSettings = window.localStorage.getItem(localStorageKey)
      if (storedSettings) {
        storedSettings = JSON.parse(storedSettings) || {}

        // We need to trigger a save after restoring from local storage
        await this.saveImpl(storedSettings, true)

        // And remove the storage as we just converted it.
        window.localStorage.removeItem(localStorageKey)

      // Read from storage.
      } else {
        storedSettings = await readJSON(this.storage, key, workspaceFile) || {}
      }
      this._hasReadStorage = true

      // Restore state.
      if (storedSettings) {
        store.replaceState(functions.merge({}, store.state, storedSettings))
      }
    } catch (e) {
      log.error('Could not restore stored settings:', e)
    }
  }

  async reset() {
    window.localStorage.removeItem(this.key)
    window.localStorage.removeItem('YM.theme')
    window.localStorage.removeItem('YM.auth')
    await this.createStorage()
    await this.storage.removePath(this.key)
    window.location.reload()
  }
}

const vuexLocalStorage = new VuexLocalStorage()
export default vuexLocalStorage

window.__yb_clearStorage = async () => {
  await vuexLocalStorage.reset()
}
